// Firebase provider — message data access.
//
// Relocated (S2) from src/features/chat/firestore.js. Behavior is identical;
// only the file location, the lazy `getDb()` call-site, and the
// normalizeMessage/sortKey/newMessageId imports change. The inline copies of
// those helpers that used to live here now come from ../shapes.js (S1), and
// subscribeMessages adapts each Firestore docSnap to the provider-agnostic
// `{ id, data }` shape before calling normalizeMessage.
//
// Reads remain tolerant of the legacy shape via normalizeMessage so old prod
// messages still render:
//   text       ?? message
//   senderId   ?? user
//   senderName ?? userName
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  limit,
} from '@firebase/firestore';
import {
  setDoc,
  doc,
  serverTimestamp,
  updateDoc,
  getDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { getDb } from './client.js';
import { normalizeMessage, sortKey, newMessageId, __test__ } from '../shapes.js';

// Hard upper bound on confirmed messages held in memory per room. The
// renderer paginates further (ChatRegion shows the newest 20 by default
// and expands by 20 on scroll-up), but we cap the in-memory set so a
// runaway room can't grow unbounded.
const MESSAGES_LIMIT = 500;
// Design constraint (F5.4): cap distinct emoji per message at 4. Enforced
// client-side only — no security rules in this app. Documented weakness.
export const MAX_DISTINCT_REACTIONS = 4;

/**
 * Subscribe to messages for a party code.
 *
 * The query is intentionally `where('code','==',code)` ONLY — no orderBy,
 * no limit. Firestore's orderBy clause implicitly filters out documents
 * missing the sort field, which would silently drop every legacy message
 * written before the Phase 05 schema migration (those docs have no sentAt).
 * Sorting client-side via sortKey() picks up both eras correctly.
 *
 * Visible-window pagination lives in ChatRegion: the renderer starts with
 * the newest 20 of these rows and expands by 20 on scroll-up. This file
 * is just the data layer — return the full sorted set (capped at
 * MESSAGES_LIMIT so memory stays bounded) and let the UI choose how much
 * of it to paint.
 *
 * Returns the unsubscribe function — callers MUST return it from their
 * effect for proper cleanup on partyCode change / unmount.
 */
export function subscribeMessages(code, cb) {
  const q = query(collection(getDb(), 'message'), where('code', '==', code));
  return onSnapshot(q, (snapshot) => {
    const rows = snapshot.docs.map((d) => normalizeMessage({ id: d.id, data: d.data() }));
    rows.sort((a, b) => sortKey(a) - sortKey(b));
    cb(rows.length > MESSAGES_LIMIT ? rows.slice(-MESSAGES_LIMIT) : rows);
  });
}

// Read-cap for the room-history scan. listMyRooms pulls every message this
// user has sent (no per-room filter) and reduces to distinct codes; the limit
// is a safety net so a hyper-chatty identity can't trigger an unbounded read.
const ROOM_HISTORY_SCAN_LIMIT = 1000;

// Coerce a raw Firestore `sentAt` (a Timestamp, a number, or absent) to epoch
// ms, or null when it can't be resolved. Unlike sortKey() we do NOT fall back
// to Date.now() — a missing timestamp means "unknown last-active", which the
// UI renders as a neutral sub-line rather than a fake "just now".
function sentAtMs(raw) {
  if (raw && typeof raw.toMillis === 'function') return raw.toMillis();
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return null;
}

/**
 * List the rooms this user has sent a message in (the "rooms I've interacted
 * in" history). Returns one entry per distinct room code with the most-recent
 * message timestamp the user sent there.
 *
 * Firestore has no DISTINCT/GROUP BY, so we pull the user's sent messages
 * (capped at ROOM_HISTORY_SCAN_LIMIT) and reduce client-side. The current
 * identity is always the new `anon-<uuid>` shape, so matching `senderId`
 * alone is correct — legacy IP-keyed `user` docs were never written by this
 * browser.
 *
 * @param {string} userId
 * @returns {Promise<Array<{ code: string, lastActive: number|null }>>}
 */
export async function listMyRooms(userId) {
  if (!userId) return [];
  const q = query(
    collection(getDb(), 'message'),
    where('senderId', '==', userId),
    limit(ROOM_HISTORY_SCAN_LIMIT),
  );
  const snap = await getDocs(q);
  const byCode = new Map(); // code -> latest sentAt (epoch ms or null)
  snap.forEach((d) => {
    const data = d.data() || {};
    const code = data.code;
    if (!code) return;
    const ts = sentAtMs(data.sentAt);
    if (!byCode.has(code)) {
      byCode.set(code, ts);
      return;
    }
    const prev = byCode.get(code);
    if (ts != null && (prev == null || ts > prev)) byCode.set(code, ts);
  });
  return [...byCode.entries()].map(([code, lastActive]) => ({ code, lastActive }));
}

/**
 * Persist a new message. Always writes the new shape; old-shape fields are
 * never written. The doc id is crypto.randomUUID() to avoid the legacy
 * Date.now() collision risk when two clients send in the same millisecond.
 */
export async function sendMessage({ code, text, senderId, senderName, avatarSeed }) {
  const id = newMessageId();
  await setDoc(doc(getDb(), 'message', id), {
    text,
    senderId,
    senderName,
    avatarSeed: avatarSeed || senderId,
    code,
    sentAt: serverTimestamp(),
    replyTo: null,
    reactions: {},
  });
  return id;
}

/**
 * Send a code-snippet attachment (F12.1).
 *
 * The chat body's `text` is intentionally empty — when an attachment is the
 * payload, the renderer pulls everything from `attachment.*`. We snapshot
 * the sender's transport choice on the doc so a recipient who renders the
 * row later (e.g. after the LAN peer drops) still sees the chip that
 * matches how the bytes actually moved.
 *
 * `transferId` is a stable handle for any future progress / dedup wiring;
 * the doc id itself is a separate UUID so a sender can reattempt the write
 * without changing the transferId visible to the receiver.
 */
export async function sendCodeSnippet({
  code,
  lang,
  title,
  text,
  senderId,
  senderName,
  avatarSeed,
  via,
}) {
  const id = newMessageId();
  const transferId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `tx-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const attachment = {
    kind: 'code',
    transferId,
    lang: String(lang || ''),
    text: String(text ?? ''),
    via: via === 'lan' ? 'lan' : 'cloud',
    fromId: senderId,
    ts: Date.now(),
  };
  if (title) attachment.title = title;

  await setDoc(doc(getDb(), 'message', id), {
    text: '',
    senderId,
    senderName,
    avatarSeed: avatarSeed || senderId,
    code,
    sentAt: serverTimestamp(),
    replyTo: null,
    reactions: {},
    attachment,
  });
  return id;
}

/**
 * Send a file attachment (F11.1+F11.2).
 *
 * Symmetric to `sendCodeSnippet` — empty `text`, attachment carries the
 * full payload. The doc id and the `transferId` are intentionally separate
 * UUIDs so a sender can retry the Firestore write (e.g. on a flake) without
 * the receiver seeing a different `transferId` and re-rendering the card.
 *
 * `via` snapshots the sender's transport pick (`'lan'` or `'cloud'`) at
 * send-time. `kind: 'file'` discriminates the attachment shape from
 * `kind: 'code'` on the read path.
 */
export async function sendFileMessage({
  code,
  transferId,
  name,
  size,
  mime,
  via,
  senderId,
  senderName,
  avatarSeed,
  fromId,
  ts,
}) {
  const id = newMessageId();
  const tid =
    transferId ||
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `tx-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const attachment = {
    kind: 'file',
    transferId: tid,
    name: String(name || 'file'),
    size: Number(size) || 0,
    mime: String(mime || 'application/octet-stream'),
    via: via === 'lan' ? 'lan' : 'cloud',
    fromId: fromId || senderId,
    ts: typeof ts === 'number' ? ts : Date.now(),
  };

  await setDoc(doc(getDb(), 'message', id), {
    text: '',
    senderId,
    senderName,
    avatarSeed: avatarSeed || senderId,
    code,
    sentAt: serverTimestamp(),
    replyTo: null,
    reactions: {},
    attachment,
  });
  return id;
}

/**
 * Toggle a single user's reaction on a message.
 *
 * Behavior:
 *   - If `userId` is already in `reactions[emoji]`, remove them (arrayRemove).
 *   - Otherwise, add them (arrayUnion).
 *   - Adding a *new* emoji when the message already has 4 distinct emojis is
 *     rejected — returns `false` and does NOT write. The caller can choose to
 *     surface a toast. (Removing an existing reaction is always allowed, even
 *     at cap.)
 *
 * Returns:
 *   - `true`  when a write was performed
 *   - `false` when the cap was hit and no write happened
 *
 * Notes:
 *   - `subscribeMessages` already streams the full doc snapshot, so chip
 *     counts update naturally on the next snapshot tick — no extra plumbing
 *     needed.
 *   - We do one read to know whether the user is in the array and to count
 *     distinct emojis for the cap check, then one write. This is two round
 *     trips but keeps logic simple — a transaction would be over-engineered
 *     for a v1 where reactions aren't a hot path.
 */
export async function toggleReaction(messageId, emoji, userId) {
  if (!messageId || !emoji || !userId) return false;
  const ref = doc(getDb(), 'message', messageId);
  const snap = await getDoc(ref);
  if (!snap.exists || (typeof snap.exists === 'function' && !snap.exists())) {
    return false;
  }
  const data = (snap.data && snap.data()) || {};
  const reactions = data.reactions || {};
  const existing = Array.isArray(reactions[emoji]) ? reactions[emoji] : [];
  const userAlreadyReacted = existing.includes(userId);

  if (!userAlreadyReacted) {
    // Adding — check the cap. Distinct emojis = keys with a non-empty array.
    const distinctActive = Object.entries(reactions).filter(
      ([, arr]) => Array.isArray(arr) && arr.length > 0,
    );
    const alreadyHasThisEmoji = distinctActive.some(([k]) => k === emoji);
    if (!alreadyHasThisEmoji && distinctActive.length >= MAX_DISTINCT_REACTIONS) {
      return false;
    }
    await updateDoc(ref, {
      [`reactions.${emoji}`]: arrayUnion(userId),
    });
    return true;
  }

  await updateDoc(ref, {
    [`reactions.${emoji}`]: arrayRemove(userId),
  });
  return true;
}

// Re-export the legacy test-import surface from shapes.js so existing tests
// resolve unchanged. (firestore.js currently exports the same triple.)
export { __test__ };
